import React, { useEffect, useState } from 'react';
import { DateInput } from '@/components/ui/date-input';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Eye, CheckCircle2, XCircle, AlertTriangle,
  ExternalLink, Save, FileText, Building2, BarChart3, AlertOctagon, Wallet, LogIn,
  Edit3, ArrowRight, Truck, UserCog, MessageSquare, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Admin page mounted at /marketplace/admin inside the existing app.
// Lists every marketplace vendor onboarded for the company, lets the
// admin approve / reject and shows compliance expiry warnings.
const MarketplaceAdminPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending_approval');
  const [expiring, setExpiring] = useState([]);
  const [settings, setSettings] = useState(null);
  const [detailVendor, setDetailVendor] = useState(null);
  const [actioning, setActioning] = useState(false);
  const [policyDraft, setPolicyDraft] = useState(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [tab, setTab] = useState('vendors'); // vendors | pos | performance | payouts
  const [adminPOs, setAdminPOs] = useState([]);
  const [poVendorFilter, setPOVendorFilter] = useState('');
  const [poStatusFilter, setPOStatusFilter] = useState('');
  const [perfRows, setPerfRows] = useState([]);
  const [penaltyTarget, setPenaltyTarget] = useState(null); // {po_id, vendor_name}
  const [penaltyDraft, setPenaltyDraft] = useState({ amount: '', reason: '' });
  const [adminPayouts, setAdminPayouts] = useState([]);
  const [payoutStatusFilter, setPayoutStatusFilter] = useState('');
  const [payoutDetail, setPayoutDetail] = useState(null);
  // Feb 2026: admin support tickets
  const [tickets, setTickets] = useState([]);
  const [ticketsOpenCount, setTicketsOpenCount] = useState(0);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('');
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [ticketActing, setTicketActing] = useState(false);
  const [adjustmentDraft, setAdjustmentDraft] = useState({ label: '', amount: '' });
  const [markPaidDraft, setMarkPaidDraft] = useState({ bank_transfer_ref: '', bank_transfer_date: '' });
  const [payoutAction, setPayoutAction] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsDraft, setTermsDraft] = useState({ content: '', version: '' });
  const [termsSaving, setTermsSaving] = useState(false);

  const openTermsEditor = async () => {
    try {
      const r = await axios.get(`${API}/marketplace/admin/terms`, { headers: authHeader() });
      setTermsDraft({ content: r.data?.content || '', version: r.data?.version || '' });
      setTermsOpen(true);
    } catch (e) {
      toast.error('Failed to load terms');
    }
  };

  const saveTerms = async () => {
    setTermsSaving(true);
    try {
      await axios.put(`${API}/marketplace/admin/terms`, termsDraft, { headers: authHeader() });
      toast.success('Terms & conditions saved');
      setTermsOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setTermsSaving(false);
    }
  };

  const loadAdminPayouts = async () => {
    try {
      const params = new URLSearchParams();
      if (payoutStatusFilter) params.set('status', payoutStatusFilter);
      const r = await axios.get(`${API}/marketplace/admin/payouts?${params}`, { headers: authHeader() });
      setAdminPayouts(r.data?.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payouts');
    }
  };

  // Open the first linked ticket for a PO (Feb 2026 pass 7). The
  // admin clicks the rose chip on the PO row → we GET the tickets for
  // that PO and open the most-recent one in the existing modal.
  const openTicketsForPO = async (po) => {
    try {
      const r = await axios.get(
        `${API}/marketplace/admin/pos/${po.id}/tickets`,
        { headers: authHeader() },
      );
      const rows = r.data?.rows || [];
      if (rows.length === 0) {
        toast.info('No tickets linked to this PO');
        return;
      }
      // Open the freshest one — they're already sorted by last_activity_at
      await openTicketDetail(rows[0].id);
    } catch (e) {
      toast.error('Failed to load tickets for this PO');
    }
  };

  // ──────── Tickets (Feb 2026) ────────

  const loadAdminTickets = async () => {
    try {
      const params = new URLSearchParams();
      if (ticketStatusFilter) params.set('status', ticketStatusFilter);
      const r = await axios.get(`${API}/marketplace/admin/tickets?${params}`, { headers: authHeader() });
      setTickets(r.data?.rows || []);
      setTicketsOpenCount(r.data?.open_count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tickets');
    }
  };

  // Eagerly load the unread count on first render so the tab badge
  // shows even before the admin clicks the Tickets tab.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/marketplace/admin/tickets`, { headers: authHeader() })
      .then((r) => { if (!cancelled) setTicketsOpenCount(r.data?.open_count || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTicketDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/marketplace/admin/tickets/${id}`, { headers: authHeader() });
      setTicketDetail(r.data);
      setTicketReply('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open ticket');
    }
  };

  const [ticketReplyFiles, setTicketReplyFiles] = useState([]);

  const ingestTicketFiles = async (files) => {
    const MAX = 4.5 * 1024 * 1024;
    const arr = Array.from(files || []);
    const out = [];
    for (const f of arr) {
      if (f.size > MAX) { toast.error(`${f.name} > 4.5MB`); continue; }
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      out.push({ filename: f.name, content_type: f.type || 'application/octet-stream', data_base64: dataUrl, size: f.size });
    }
    if (out.length) setTicketReplyFiles((p) => [...p, ...out].slice(0, 5));
  };

  const downloadAdminAttachment = async (msgId, att) => {
    try {
      const r = await axios.get(
        `${API}/marketplace/admin/tickets/${ticketDetail.id}/attachments/${msgId}/${att.id}`,
        { headers: authHeader() },
      );
      const a = document.createElement('a');
      a.href = `data:${r.data.content_type};base64,${r.data.data_base64}`;
      a.download = r.data.filename || att.filename;
      a.click();
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const submitTicketReply = async () => {
    if (!ticketReply.trim()) return;
    setTicketActing(true);
    try {
      await axios.post(
        `${API}/marketplace/admin/tickets/${ticketDetail.id}/reply`,
        { message: ticketReply.trim(), attachments: ticketReplyFiles },
        { headers: authHeader() },
      );
      toast.success('Reply sent to vendor');
      setTicketReply('');
      setTicketReplyFiles([]);
      await openTicketDetail(ticketDetail.id);
      loadAdminTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed');
    } finally {
      setTicketActing(false);
    }
  };

  const setTicketStatus = async (next) => {
    setTicketActing(true);
    try {
      await axios.patch(
        `${API}/marketplace/admin/tickets/${ticketDetail.id}/status`,
        { status: next },
        { headers: authHeader() },
      );
      toast.success(`Marked ${next.replace('_', ' ')}`);
      await openTicketDetail(ticketDetail.id);
      loadAdminTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status update failed');
    } finally {
      setTicketActing(false);
    }
  };

  const openPayoutDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/marketplace/admin/payouts/${id}`, { headers: authHeader() });
      setPayoutDetail(r.data);
      setAdjustmentDraft({ label: '', amount: '' });
      setMarkPaidDraft({ bank_transfer_ref: '', bank_transfer_date: new Date().toISOString().slice(0, 10) });
    } catch (e) {
      toast.error('Failed to load payout');
    }
  };

  const addAdjustment = async () => {
    if (!payoutDetail) return;
    if (!adjustmentDraft.label.trim()) { toast.error('Label required'); return; }
    const amount = parseFloat(adjustmentDraft.amount);
    if (Number.isNaN(amount) || amount === 0) { toast.error('Amount required (non-zero, negative allowed)'); return; }
    setPayoutAction(true);
    try {
      await axios.post(
        `${API}/marketplace/admin/payouts/${payoutDetail.id}/adjustments`,
        { label: adjustmentDraft.label.trim(), amount },
        { headers: authHeader() },
      );
      toast.success('Adjustment added');
      setAdjustmentDraft({ label: '', amount: '' });
      await openPayoutDetail(payoutDetail.id);
      await loadAdminPayouts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setPayoutAction(false); }
  };

  const removeAdjustment = async (adj_id) => {
    if (!payoutDetail) return;
    setPayoutAction(true);
    try {
      await axios.delete(
        `${API}/marketplace/admin/payouts/${payoutDetail.id}/adjustments/${adj_id}`,
        { headers: authHeader() },
      );
      await openPayoutDetail(payoutDetail.id);
      await loadAdminPayouts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setPayoutAction(false); }
  };

  const payoutTransition = async (action, body) => {
    if (!payoutDetail) return;
    setPayoutAction(true);
    try {
      const r = await axios.post(
        `${API}/marketplace/admin/payouts/${payoutDetail.id}/${action}`,
        body || {},
        { headers: authHeader() },
      );
      toast.success(`Payout ${r.data.status}`);
      if (action === 'reject' || action === 'mark-paid') {
        setPayoutDetail(null);
      } else {
        await openPayoutDetail(payoutDetail.id);
      }
      await loadAdminPayouts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setPayoutAction(false); }
  };

  const loadPOs = async () => {
    try {
      const params = new URLSearchParams();
      if (poVendorFilter) params.set('vendor_id', poVendorFilter);
      if (poStatusFilter) params.set('status', poStatusFilter);
      const r = await axios.get(`${API}/marketplace/admin/pos?${params.toString()}`, { headers: authHeader() });
      setAdminPOs(r.data?.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load POs');
    }
  };

  const loadPerf = async () => {
    try {
      const r = await axios.get(`${API}/marketplace/admin/performance`, { headers: authHeader() });
      setPerfRows(r.data?.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load performance');
    }
  };

  const applyPenalty = async () => {
    if (!penaltyTarget) return;
    const amount = parseFloat(penaltyDraft.amount);
    if (!amount || amount <= 0) { toast.error('Enter a positive amount'); return; }
    if (!penaltyDraft.reason.trim()) { toast.error('Reason required'); return; }
    try {
      await axios.post(
        `${API}/marketplace/admin/pos/${penaltyTarget.po_id}/penalty`,
        { amount, reason: penaltyDraft.reason.trim() },
        { headers: authHeader() },
      );
      toast.success(`Penalty of ${amount} applied`);
      setPenaltyTarget(null);
      setPenaltyDraft({ amount: '', reason: '' });
      loadPOs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply penalty');
    }
  };

  const bookCourier = async (po) => {
    try {
      toast.loading('Booking courier…', { id: `book-${po.id}` });
      const r = await axios.post(
        `${API}/marketplace/admin/pos/${po.id}/book-courier`,
        {},
        { headers: authHeader() },
      );
      toast.dismiss(`book-${po.id}`);
      if (r.data?.awb_number) {
        toast.success(`Booked · AWB ${r.data.awb_number}`);
      } else {
        toast.error(`Could not book: ${r.data?.auto_book_error || r.data?.auto_book_status || 'unknown error'}`);
      }
      loadPOs();
    } catch (e) {
      toast.dismiss(`book-${po.id}`);
      toast.error(e.response?.data?.detail || 'Booking failed');
    }
  };

  // Assign-driver state (for company-pickup marketplace POs)
  const [assignDriverTarget, setAssignDriverTarget] = useState(null); // {po_id, order_number, vendor_name}
  const [driverList, setDriverList] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [assigningDriver, setAssigningDriver] = useState(false);

  const openAssignDriver = async (po) => {
    setAssignDriverTarget({ po_id: po.id, order_number: po.order_number, vendor_name: po.vendor_name, current_driver: po.assigned_driver_name });
    setSelectedDriverId('');
    try {
      const r = await axios.get(`${API}/drivers`, { headers: authHeader() });
      setDriverList(r.data?.drivers || r.data || []);
    } catch (e) {
      toast.error('Failed to load drivers');
    }
  };

  const submitAssignDriver = async () => {
    if (!selectedDriverId) {
      toast.error('Select a driver first');
      return;
    }
    setAssigningDriver(true);
    try {
      const r = await axios.post(
        `${API}/marketplace/admin/pos/${assignDriverTarget.po_id}/assign-driver`,
        { driver_id: selectedDriverId },
        { headers: authHeader() },
      );
      toast.success(`Assigned to ${r.data.assigned_driver_name}`);
      setAssignDriverTarget(null);
      setSelectedDriverId('');
      loadPOs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign driver');
    } finally {
      setAssigningDriver(false);
    }
  };

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const [q, exp, st] = await Promise.all([
        axios.get(`${API}/marketplace/admin/onboarding-queue?status_filter=${statusFilter}`, { headers: authHeader() }),
        axios.get(`${API}/marketplace/admin/expiring-documents?days=30`, { headers: authHeader() }),
        axios.get(`${API}/marketplace/admin/onboarding-settings`, { headers: authHeader() }),
      ]);
      setRows(q.data?.rows || []);
      setExpiring(exp.data?.rows || []);
      setSettings(st.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/marketplace/admin/vendor/${id}`, { headers: authHeader() });
      setDetailVendor(r.data);
      setPolicyDraft({
        default_commission_pct: r.data.default_commission_pct ?? 0,
        allow_edit_cost_price: r.data.allow_edit_cost_price !== false,
        allow_edit_quantity: r.data.allow_edit_quantity === true,
        fulfillment_tat_days: r.data.fulfillment_tat_days ?? 3,
      });
    } catch (e) {
      toast.error('Failed to fetch vendor');
    }
  };

  const savePolicy = async () => {
    if (!detailVendor || !policyDraft) return;
    setSavingPolicy(true);
    try {
      await axios.put(
        `${API}/marketplace/admin/vendor/${detailVendor.id}/policy`,
        {
          default_commission_pct: parseFloat(policyDraft.default_commission_pct) || 0,
          allow_edit_cost_price: !!policyDraft.allow_edit_cost_price,
          allow_edit_quantity: !!policyDraft.allow_edit_quantity,
          fulfillment_tat_days: parseInt(policyDraft.fulfillment_tat_days, 10) || 3,
        },
        { headers: authHeader() },
      );
      toast.success('Policy updated');
      setDetailVendor({ ...detailVendor, ...policyDraft });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingPolicy(false);
    }
  };

  const approve = async (id) => {
    if (!window.confirm('Approve this vendor and enable their portal login?')) return;
    setActioning(true);
    try {
      await axios.post(`${API}/marketplace/admin/vendor/${id}/approve`, {}, { headers: authHeader() });
      toast.success('Vendor approved');
      setDetailVendor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally {
      setActioning(false);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason (will be saved for the audit log):', '');
    if (reason === null) return;
    setActioning(true);
    try {
      await axios.post(`${API}/marketplace/admin/vendor/${id}/reject`, { reason }, { headers: authHeader() });
      toast.success('Vendor rejected');
      setDetailVendor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    } finally {
      setActioning(false);
    }
  };

  const saveSettings = async (next) => {
    try {
      const body = {
        enabled: next.enabled,
        headline: next.headline,
        sub_headline: next.sub_headline,
        logo_url: next.logo_url || null,
        primary_color: next.primary_color || '#D4AF37',
        accept_blurb: next.accept_blurb || null,
        rejection_email_template: next.rejection_email_template || null,
      };
      await axios.put(`${API}/marketplace/admin/onboarding-settings`, body, { headers: authHeader() });
      setSettings({ ...settings, ...body });
      toast.success('Onboarding page customized');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const onboardUrl = settings ? `${window.location.origin}${settings.onboarding_url_path}` : '';
  // Per-tenant vendor login URL — branded sister of the onboarding link.
  const loginUrl = settings?.company_id ? `${window.location.origin}/marketplace-vendor/login/${settings.company_id}` : '';

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="marketplace-admin-page">
      <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-playfair text-wt-text-primary flex items-center gap-2">
              <Building2 className="w-6 h-6 text-wt-gold" />
              Marketplace Vendors
            </h1>
            <p className="text-sm text-wt-text-muted mt-1">Onboard external sellers and manage their compliance + portal access.</p>
          </div>
          <button onClick={load} className="text-xs text-wt-text-muted hover:text-wt-text-primary inline-flex items-center gap-1.5">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Top-level tabs */}
        <div className="flex items-center gap-1 border-b border-wt-border" data-testid="mp-admin-tabs">
          <AdminTab id="vendors" tab={tab} setTab={setTab} icon={<Building2 className="w-3.5 h-3.5" />}>Vendors</AdminTab>
          <AdminTab id="pos" tab={tab} setTab={(t) => { setTab(t); loadPOs(); }} icon={<FileText className="w-3.5 h-3.5" />}>Purchase Orders</AdminTab>
          <AdminTab id="performance" tab={tab} setTab={(t) => { setTab(t); loadPerf(); }} icon={<BarChart3 className="w-3.5 h-3.5" />}>Performance</AdminTab>
          <AdminTab id="payouts" tab={tab} setTab={(t) => { setTab(t); loadAdminPayouts(); }} icon={<Wallet className="w-3.5 h-3.5" />}>Payouts</AdminTab>
          <AdminTab
            id="tickets"
            tab={tab}
            setTab={(t) => { setTab(t); loadAdminTickets(); }}
            icon={<MessageSquare className="w-3.5 h-3.5" />}
          >
            Tickets{ticketsOpenCount > 0 && (
              <span className="ml-1 bg-rose-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold" data-testid="mp-admin-tickets-badge">
                {ticketsOpenCount}
              </span>
            )}
          </AdminTab>
        </div>

        {tab !== 'vendors' ? null : (<></>)}

        {tab === 'vendors' && (<>
        {/* Onboarding link share box */}
        {settings && (
          <div className="bg-wt-surface border border-wt-border rounded-xl p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="text-base font-semibold">Onboarding Link</h2>
                <p className="text-xs text-wt-text-muted">Share this URL with prospective vendors. They self-onboard, you approve.</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={openTermsEditor}
                  className="px-3 py-1.5 rounded border border-wt-border text-xs text-wt-text-primary hover:bg-wt-paper inline-flex items-center gap-1"
                  data-testid="mp-admin-edit-terms"
                >
                  <FileText className="w-3.5 h-3.5" /> Edit Terms &amp; Conditions
                </button>
                <label className="inline-flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={settings.enabled !== false}
                    onChange={(e) => saveSettings({ ...settings, enabled: e.target.checked })}
                    data-testid="mp-admin-enable-toggle"
                  />
                  <span>Public link enabled</span>
                </label>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <input
                value={onboardUrl}
                readOnly
                className="flex-1 px-3 py-2 rounded bg-wt-paper border border-wt-border text-wt-text-primary font-mono text-xs"
                data-testid="mp-admin-onboard-url"
              />
              <button
                onClick={() => { navigator.clipboard.writeText(onboardUrl); toast.success('Copied'); }}
                className="px-3 py-2 rounded bg-wt-gold text-wt-paper hover:bg-wt-gold/90 text-xs font-semibold"
                data-testid="mp-admin-copy-url"
              >
                Copy
              </button>
              <a
                href={onboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded border border-wt-border text-wt-text-muted hover:text-wt-text-primary text-xs inline-flex items-center gap-1"
              >
                Preview <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            {/* Per-tenant vendor login URL (Phase 4.1) — share this with
                approved vendors so they land on a branded sign-in page. */}
            {loginUrl && (
              <div className="border-t border-wt-border pt-4 mb-4">
                <p className="text-xs text-wt-text-muted mb-2 flex items-center gap-1">
                  <LogIn className="w-3 h-3" />
                  Vendor sign-in URL (branded with your logo &amp; colors — share with approved vendors)
                </p>
                <div className="flex items-center gap-2">
                  <input
                    value={loginUrl}
                    readOnly
                    className="flex-1 px-3 py-2 rounded bg-wt-paper border border-wt-border text-wt-text-primary font-mono text-xs"
                    data-testid="mp-admin-vendor-login-url"
                  />
                  <button
                    onClick={() => { navigator.clipboard.writeText(loginUrl); toast.success('Copied'); }}
                    className="px-3 py-2 rounded bg-wt-gold text-wt-paper hover:bg-wt-gold/90 text-xs font-semibold"
                    data-testid="mp-admin-copy-login-url"
                  >
                    Copy
                  </button>
                  <a
                    href={loginUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded border border-wt-border text-wt-text-muted hover:text-wt-text-primary text-xs inline-flex items-center gap-1"
                  >
                    Preview <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SettingsField label="Headline" value={settings.headline} onChange={(v) => setSettings({ ...settings, headline: v })} onBlur={() => saveSettings(settings)} />
              <SettingsField label="Sub-headline" value={settings.sub_headline} onChange={(v) => setSettings({ ...settings, sub_headline: v })} onBlur={() => saveSettings(settings)} />
              <SettingsField label="Logo URL (optional)" value={settings.logo_url || ''} onChange={(v) => setSettings({ ...settings, logo_url: v })} onBlur={() => saveSettings(settings)} />
              <SettingsField label="Primary color (hex)" value={settings.primary_color} onChange={(v) => setSettings({ ...settings, primary_color: v })} onBlur={() => saveSettings(settings)} />
            </div>
          </div>
        )}

        {/* Visibility settings (Feb 2026) — control whether vendors
            see their commission % + customer-facing selling price. */}
        {statusFilter === 'approved' && (
          <>
            <SyncErrorBanner authHeader={authHeader} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <VisibilitySettingsCard authHeader={authHeader} />
              <DefaultWarehouseCard authHeader={authHeader} />
            </div>
          </>
        )}

        {/* Expiring docs warning */}
        {expiring.length > 0 && (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-xl p-4">
            <div className="flex items-center gap-2 text-amber-400 font-semibold mb-2 text-sm">
              <AlertTriangle className="w-4 h-4" /> {expiring.length} vendor{expiring.length === 1 ? '' : 's'} with documents expiring in ≤30 days
            </div>
            <ul className="text-xs text-wt-text-muted space-y-1">
              {expiring.slice(0, 5).map((e) => (
                <li key={e.id}>
                  <b className="text-wt-text-primary">{e.vendor_name}</b>:
                  {e.trade_license_expiry && (
                    <span className={`ml-2 ${e.trade_license_expiry_is_expired ? 'text-rose-400' : 'text-amber-300'}`}>
                      Trade License → {e.trade_license_expiry} ({e.trade_license_expiry_days_left}d)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Vendor list */}
        <div className="bg-wt-paper border border-wt-border rounded-xl">
          <div className="border-b border-wt-border px-5 py-3 flex items-center gap-2">
            {['pending_approval', 'approved', 'rejected'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded text-xs font-semibold ${
                  statusFilter === s ? 'bg-wt-gold text-wt-paper' : 'bg-wt-paper text-wt-text-muted hover:text-wt-text-primary'
                }`}
                data-testid={`mp-admin-tab-${s}`}
              >
                {s.replace('_', ' ')}
              </button>
            ))}
            <span className="text-[10px] text-wt-text-muted ml-auto" data-testid="mp-admin-vendor-count">
              {rows.length} vendor{rows.length === 1 ? '' : 's'}
            </span>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-wt-text-muted" /></div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-wt-text-muted">No vendors with status: {statusFilter.replace('_', ' ')}</div>
          ) : (
            <div className="divide-y divide-wt-border" data-testid="mp-admin-vendor-list">
              {rows.map((v) => {
                const isApproved = (v.marketplace_status || statusFilter) === 'approved';
                return (
                  <div
                    key={v.id}
                    className="px-5 py-4 flex items-center gap-4 hover:bg-wt-subtle/40 transition-colors"
                    data-testid={`mp-admin-vendor-row-${v.id}`}
                  >
                    {/* Avatar bubble */}
                    <div className="w-10 h-10 rounded-full bg-wt-gold/20 text-wt-gold font-semibold flex items-center justify-center shrink-0 text-sm">
                      {(v.vendor_name || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-wt-text-primary truncate">{v.vendor_name}</div>
                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold ${
                          (v.marketplace_status || statusFilter) === 'approved' ? 'bg-emerald-500/15 text-emerald-300' :
                          (v.marketplace_status || statusFilter) === 'rejected' ? 'bg-rose-500/15 text-rose-300' :
                          'bg-amber-500/15 text-amber-300'
                        }`}>
                          {(v.marketplace_status || statusFilter).replace('_', ' ')}
                        </span>
                        {v.vendor_code && (
                          <span className="text-xs text-wt-gold font-mono">{v.vendor_code}</span>
                        )}
                      </div>
                      <div className="text-xs text-wt-text-muted truncate mt-0.5">
                        {v.email} · {v.phone} · {v.city || v.country || '—'}
                      </div>
                      <div className="text-[10px] text-wt-text-muted mt-0.5">
                        TL: {v.trade_license_number || '—'}
                        {v.default_commission_pct !== undefined && v.default_commission_pct !== null && (
                          <span className="ml-2 text-wt-gold">· {v.default_commission_pct}% commission</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isApproved && (
                        <button
                          onClick={() => navigate(`/marketplace-admin/vendors/${v.id}`)}
                          className="px-3 py-1.5 rounded bg-wt-gold/10 text-wt-gold hover:bg-wt-gold/20 text-xs font-semibold inline-flex items-center gap-1.5"
                          data-testid={`mp-admin-vendor-detail-${v.id}`}
                          title="View performance & ledger"
                        >
                          <BarChart3 className="w-3.5 h-3.5" /> Performance &amp; Ledger
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={() => openDetail(v.id)}
                        className="px-3 py-1.5 rounded border border-wt-border text-wt-text-primary hover:bg-wt-subtle text-xs font-semibold inline-flex items-center gap-1.5"
                        data-testid={`mp-admin-view-${v.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" /> {isApproved ? 'Quick view' : 'Review'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </>)}

        {tab === 'pos' && (
          <AdminPOsSection
            adminPOs={adminPOs}
            poVendorFilter={poVendorFilter}
            poStatusFilter={poStatusFilter}
            setPOVendorFilter={setPOVendorFilter}
            setPOStatusFilter={setPOStatusFilter}
            loadPOs={loadPOs}
            onPenalty={(po) => setPenaltyTarget({ po_id: po.id, vendor_name: po.vendor_name, order_number: po.order_number })}
            onBookCourier={bookCourier}
            onAssignDriver={openAssignDriver}
            onOpenLinkedTickets={openTicketsForPO}
          />
        )}

        {tab === 'performance' && (
          <AdminPerformanceSection perfRows={perfRows} reload={loadPerf} />
        )}

        {tab === 'payouts' && (
          <AdminPayoutsSection
            rows={adminPayouts}
            statusFilter={payoutStatusFilter}
            setStatusFilter={setPayoutStatusFilter}
            reload={loadAdminPayouts}
            onOpen={openPayoutDetail}
          />
        )}

        {tab === 'tickets' && (
          <AdminTicketsSection
            rows={tickets}
            statusFilter={ticketStatusFilter}
            setStatusFilter={(s) => { setTicketStatusFilter(s); }}
            reload={loadAdminTickets}
            onOpen={openTicketDetail}
          />
        )}

      {/* Ticket detail modal (Feb 2026) */}
      {ticketDetail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setTicketDetail(null)}>
          <div
            className="bg-wt-paper border border-wt-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="mp-admin-ticket-detail-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-wt-gold" />
                  {ticketDetail.subject}
                </h2>
                <p className="text-xs text-wt-text-muted mt-1">
                  {ticketDetail.vendor_name} · <span className="capitalize">{(ticketDetail.category || '').replace('_', ' ')}</span>
                  {ticketDetail.related_po_number && (
                    <span className="ml-2 text-wt-gold font-mono">PO {ticketDetail.related_po_number}</span>
                  )}
                </p>
              </div>
              <button onClick={() => setTicketDetail(null)} className="text-wt-text-muted hover:text-wt-text-primary">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Status changer */}
            <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-wt-border">
              <span className="text-xs text-wt-text-muted">Status:</span>
              {['open', 'in_progress', 'resolved', 'closed'].map((s) => (
                <button
                  key={s}
                  onClick={() => setTicketStatus(s)}
                  disabled={ticketActing || ticketDetail.status === s}
                  className={`px-2.5 py-1 rounded text-[10px] uppercase font-semibold ${
                    ticketDetail.status === s
                      ? 'bg-wt-gold text-black'
                      : 'bg-wt-subtle text-wt-text-muted hover:text-wt-text-primary'
                  } disabled:opacity-50`}
                  data-testid={`mp-admin-ticket-status-${s}`}
                >
                  {s.replace('_', ' ')}
                </button>
              ))}
            </div>

            {/* Thread */}
            <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
              {(ticketDetail.messages || []).map((m) => (
                <div
                  key={m.id}
                  className={`p-3 rounded-lg ${m.author === 'admin' ? 'bg-wt-gold/10 mr-6' : 'bg-wt-subtle ml-6'}`}
                  data-testid={`mp-admin-ticket-msg-${m.id}`}
                >
                  <div className="text-[10px] text-wt-text-muted mb-1">
                    {m.author === 'admin' ? `Support (${m.author_name || 'you'})` : `Vendor (${m.author_name || ticketDetail.vendor_name})`}
                    {' · '}{(m.created_at || '').replace('T', ' ').slice(0, 16)}
                  </div>
                  <div className="text-sm whitespace-pre-wrap text-wt-text-primary">{m.message}</div>
                  {m.attachments?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {m.attachments.map((a) => (
                        <li key={a.id}>
                          <button
                            type="button"
                            onClick={() => downloadAdminAttachment(m.id, a)}
                            className="flex items-center gap-2 text-[11px] text-wt-gold hover:underline"
                            data-testid={`mp-admin-ticket-attach-${a.id}`}
                          >
                            <FileText className="w-3 h-3" />
                            <span>{a.filename}</span>
                            <span className="text-wt-text-muted">{Math.round((a.size || 0) / 1024)}KB</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Reply box — disabled for closed tickets */}
            {ticketDetail.status !== 'closed' ? (
              <div className="space-y-2">
                <textarea
                  value={ticketReply}
                  onChange={(e) => setTicketReply(e.target.value)}
                  rows={3}
                  placeholder="Reply to vendor…"
                  className="w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm"
                  data-testid="mp-admin-ticket-reply-input"
                />
                <div className="flex items-center justify-between gap-2">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => ingestTicketFiles(e.target.files)}
                    className="text-[10px] text-wt-text-muted"
                    data-testid="mp-admin-ticket-reply-attachments"
                  />
                  <button
                    onClick={submitTicketReply}
                    disabled={ticketActing || !ticketReply.trim()}
                    className="px-3 py-1.5 rounded bg-wt-gold text-black text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="mp-admin-ticket-reply-submit"
                  >
                    <Save className="w-3 h-3" /> {ticketActing ? 'Sending…' : 'Send reply'}
                  </button>
                </div>
                {ticketReplyFiles.length > 0 && (
                  <ul className="text-[11px] text-wt-text-muted space-y-1">
                    {ticketReplyFiles.map((a, i) => (
                      <li key={i} className="flex items-center gap-2 bg-wt-bg rounded px-2 py-1">
                        <FileText className="w-3 h-3 text-wt-gold" />
                        <span className="truncate flex-1">{a.filename}</span>
                        <button onClick={() => setTicketReplyFiles((p) => p.filter((_, j) => j !== i))} className="text-rose-400 hover:text-rose-300">
                          <X className="w-3 h-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="text-xs text-wt-text-muted bg-wt-subtle/40 rounded p-2 text-center">
                Ticket is closed. Reopen it to add more messages.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail / approval modal */}
      {detailVendor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setDetailVendor(null)}>
          <div
            className="bg-wt-paper border border-wt-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="mp-admin-detail-modal"
          >
            <h2 className="text-xl font-semibold mb-1">{detailVendor.vendor_name}</h2>
            <p className="text-xs text-wt-text-muted mb-4">{detailVendor.email} · {detailVendor.phone}</p>

            <DetailRow label="Country" value={detailVendor.country} />
            <DetailRow label="City" value={detailVendor.city} />
            <DetailRow
              label="Pickup Address"
              value={(() => {
                const pa = detailVendor.pickup_address;
                if (!pa) return detailVendor.pickup_address_text || '—';
                if (typeof pa === 'string') return pa;
                // Structured shape — render as a readable single line.
                const parts = [pa.warehouse_name, pa.line1, pa.line2, pa.city, pa.state, pa.pincode, pa.country]
                  .filter(Boolean);
                return parts.length ? parts.join(', ') : (detailVendor.pickup_address_text || '—');
              })()}
            />
            <DetailRow label="Trade License" value={`${detailVendor.trade_license_number} · expires ${detailVendor.trade_license_expiry}`} />
            <DetailRow label="Tax Certificate" value={detailVendor.tax_certificate_number ? `${detailVendor.tax_certificate_number} · expires ${detailVendor.tax_certificate_expiry}` : '—'} />
            <DetailRow
              label="Bank"
              value={(() => {
                const b = detailVendor.bank_details || {};
                if (!b.bank_name && !b.iban && !b.ifsc && !b.account_number) return '—';
                // Country-aware identifier preference (matches Phase 4.1 schema)
                const id = b.iban || b.ifsc || b.routing_number || b.sort_code || b.account_number || '';
                return [b.bank_name, id].filter(Boolean).join(' · ') || '—';
              })()}
            />
            <DetailRow label="Username" value={detailVendor.portal_username} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <DocPreview
                label="Trade License Document"
                base64={detailVendor.trade_license_doc_base64}
                filename={`${(detailVendor.vendor_name || 'vendor').replace(/[^a-z0-9]/gi, '-')}-trade-license.pdf`}
              />
              <DocPreview
                label="Tax Certificate"
                base64={detailVendor.tax_certificate_doc_base64}
                filename={`${(detailVendor.vendor_name || 'vendor').replace(/[^a-z0-9]/gi, '-')}-tax-certificate.pdf`}
              />
            </div>

            {/* Password reset request — visible only if the vendor
                clicked "Forgot password" on the login page. */}
            {detailVendor.marketplace_status === 'approved' && detailVendor.password_reset_requested_at && (
              <div className="mt-4 p-3 rounded border border-rose-500/30 bg-rose-500/10 flex items-center justify-between gap-3" data-testid="mp-admin-pwd-reset-banner">
                <div className="text-xs text-rose-300">
                  <b>Password reset requested</b> on {new Date(detailVendor.password_reset_requested_at).toLocaleString()}
                </div>
                <button
                  onClick={() => {
                    const np = window.prompt('Enter a NEW password for this vendor (min 8 characters). Share it with them out-of-band.');
                    if (!np) return;
                    if (np.length < 8) { toast.error('Password must be at least 8 characters'); return; }
                    axios.post(`${API}/marketplace/admin/vendor/${detailVendor.id}/reset-password`,
                      { new_password: np }, { headers: authHeader() })
                      .then(() => { toast.success('Password reset'); openDetail(detailVendor.id); load(); })
                      .catch((e) => toast.error(e.response?.data?.detail || 'Reset failed'));
                  }}
                  className="px-3 py-1.5 rounded bg-rose-500 text-slate-950 text-xs font-semibold"
                  data-testid="mp-admin-reset-password"
                >
                  Reset password
                </button>
              </div>
            )}

            {/* Per-vendor commission / permission policy (approved vendors only) */}
            {detailVendor.marketplace_status === 'approved' && policyDraft && (
              <div className="mt-6 border border-wt-gold/30 bg-wt-gold/5 rounded-lg p-4" data-testid="mp-admin-policy-card">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-wt-gold">Commission &amp; permissions</h3>
                    <p className="text-[11px] text-wt-text-muted">Controls what the vendor can edit and how Shopify selling price is computed.</p>
                  </div>
                  <button
                    onClick={savePolicy}
                    disabled={savingPolicy}
                    className="px-3 py-1.5 rounded bg-wt-gold text-wt-paper text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="mp-admin-policy-save"
                  >
                    <Save className="w-3.5 h-3.5" /> {savingPolicy ? 'Saving…' : 'Save policy'}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <label className="text-wt-text-muted">
                    Default commission %
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={policyDraft.default_commission_pct}
                      onChange={(e) => setPolicyDraft({ ...policyDraft, default_commission_pct: e.target.value })}
                      className="mt-1 w-full px-3 py-2 rounded bg-wt-paper border border-wt-border text-wt-text-primary text-sm"
                      data-testid="mp-admin-policy-commission"
                    />
                  </label>
                  <label className="text-wt-text-muted">
                    Fulfillment TAT (days)
                    <input
                      type="number"
                      min="1"
                      value={policyDraft.fulfillment_tat_days}
                      onChange={(e) => setPolicyDraft({ ...policyDraft, fulfillment_tat_days: e.target.value })}
                      className="mt-1 w-full px-3 py-2 rounded bg-wt-paper border border-wt-border text-wt-text-primary text-sm"
                      data-testid="mp-admin-policy-tat"
                    />
                  </label>
                  <label className="inline-flex items-center gap-2 text-wt-text-primary mt-1">
                    <input
                      type="checkbox"
                      checked={!!policyDraft.allow_edit_cost_price}
                      onChange={(e) => setPolicyDraft({ ...policyDraft, allow_edit_cost_price: e.target.checked })}
                      data-testid="mp-admin-policy-allow-cost"
                    />
                    Allow vendor to edit cost price
                  </label>
                  <label className="inline-flex items-center gap-2 text-wt-text-primary mt-1">
                    <input
                      type="checkbox"
                      checked={!!policyDraft.allow_edit_quantity}
                      onChange={(e) => setPolicyDraft({ ...policyDraft, allow_edit_quantity: e.target.checked })}
                      data-testid="mp-admin-policy-allow-qty"
                    />
                    Allow vendor to edit quantity
                  </label>
                </div>
              </div>
            )}

            {detailVendor.marketplace_status === 'pending_approval' ? (
              <div className="mt-6 flex items-center gap-3">
                <button
                  onClick={() => approve(detailVendor.id)}
                  disabled={actioning}
                  className="px-4 py-2 rounded bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="mp-admin-approve-btn"
                >
                  <CheckCircle2 className="w-4 h-4" /> Approve
                </button>
                <button
                  onClick={() => reject(detailVendor.id)}
                  disabled={actioning}
                  className="px-4 py-2 rounded bg-rose-500 hover:bg-rose-400 text-slate-950 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="mp-admin-reject-btn"
                >
                  <XCircle className="w-4 h-4" /> Reject
                </button>
                <button onClick={() => setDetailVendor(null)} className="text-xs text-wt-text-muted ml-auto">Close</button>
              </div>
            ) : (
              <div className="mt-6 text-xs text-wt-text-muted">
                Status: <b className="uppercase">{detailVendor.marketplace_status}</b>
                {detailVendor.rejected_reason && <span> · reason: {detailVendor.rejected_reason}</span>}
                <button onClick={() => setDetailVendor(null)} className="float-right">Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Terms & Conditions editor (Phase 4.1) */}
      {termsOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setTermsOpen(false)}>
          <div
            className="bg-wt-paper border border-wt-border rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="mp-admin-terms-modal"
          >
            <div className="px-6 py-4 border-b border-wt-border flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-wt-gold" /> Marketplace Terms &amp; Conditions
                </h2>
                <p className="text-xs text-wt-text-muted mt-0.5">
                  Vendors must accept this before they can submit the onboarding form. Leave empty to hide the checkbox.
                </p>
              </div>
              <button onClick={() => setTermsOpen(false)} className="text-wt-text-muted hover:text-wt-text-primary">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
              <label className="block text-xs text-wt-text-muted">
                Version label (optional — auto-stamped with today&apos;s date if left blank)
                <input
                  value={termsDraft.version}
                  onChange={(e) => setTermsDraft({ ...termsDraft, version: e.target.value })}
                  placeholder="e.g. v1.0 / 2026-06-06"
                  className="mt-1 w-full px-3 py-2 rounded bg-wt-paper border border-wt-border text-sm"
                  data-testid="mp-admin-terms-version"
                />
              </label>
              <label className="block text-xs text-wt-text-muted">
                Terms content
                <textarea
                  value={termsDraft.content}
                  onChange={(e) => setTermsDraft({ ...termsDraft, content: e.target.value })}
                  rows={18}
                  placeholder="Paste your full marketplace vendor agreement here…"
                  className="mt-1 w-full px-3 py-2 rounded bg-wt-paper border border-wt-border text-sm font-mono leading-relaxed"
                  data-testid="mp-admin-terms-content"
                />
              </label>
            </div>
            <div className="px-6 py-3 border-t border-wt-border flex justify-end gap-2">
              <button onClick={() => setTermsOpen(false)} className="px-3 py-2 text-xs text-wt-text-muted hover:text-wt-text-primary">
                Cancel
              </button>
              <button
                onClick={saveTerms}
                disabled={termsSaving}
                className="px-4 py-2 rounded bg-wt-gold text-wt-paper text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="mp-admin-terms-save"
              >
                {termsSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save terms
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Penalty dialog (Phase 3) */}
      {penaltyTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPenaltyTarget(null)}>
          <div
            className="bg-wt-paper border border-wt-border rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="mp-admin-penalty-modal"
          >
            <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <AlertOctagon className="w-5 h-5 text-rose-400" /> Apply penalty
            </h2>
            <p className="text-xs text-wt-text-muted mb-4">
              {penaltyTarget.vendor_name} · order {penaltyTarget.order_number}
            </p>
            <label className="text-xs text-wt-text-muted block mb-2">
              Amount
              <input
                type="number"
                step="0.01"
                min="0"
                value={penaltyDraft.amount}
                onChange={(e) => setPenaltyDraft({ ...penaltyDraft, amount: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded bg-wt-paper border border-wt-border text-wt-text-primary text-sm"
                data-testid="mp-admin-penalty-amount"
              />
            </label>
            <label className="text-xs text-wt-text-muted block mb-4">
              Reason
              <textarea
                value={penaltyDraft.reason}
                onChange={(e) => setPenaltyDraft({ ...penaltyDraft, reason: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded bg-wt-paper border border-wt-border text-wt-text-primary text-sm h-20"
                placeholder="e.g. Late acceptance, defective batch, missed pickup window…"
                data-testid="mp-admin-penalty-reason"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPenaltyTarget(null)}
                className="px-3 py-1.5 text-xs text-wt-text-muted hover:text-wt-text-primary"
              >Cancel</button>
              <button
                onClick={applyPenalty}
                className="px-4 py-2 rounded bg-rose-500 hover:bg-rose-400 text-slate-950 text-sm font-semibold"
                data-testid="mp-admin-penalty-apply"
              >Apply penalty</button>
            </div>
          </div>
        </div>
      )}
      {/* Assign-Driver modal — only opens for company-pickup POs once
          the vendor has marked Ready For Pickup. */}
      {assignDriverTarget && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setAssignDriverTarget(null)}
        >
          <div
            className="bg-wt-paper border border-wt-border rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="mp-admin-assign-driver-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Truck className="w-5 h-5 text-wt-gold" />
                {assignDriverTarget.current_driver ? 'Reassign driver' : 'Assign driver'}
              </h2>
              <button
                onClick={() => setAssignDriverTarget(null)}
                className="text-wt-text-muted hover:text-wt-text-primary"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-wt-text-muted mb-1">
              PO <span className="text-wt-gold font-mono">{assignDriverTarget.order_number}</span> · {assignDriverTarget.vendor_name}
            </p>
            {assignDriverTarget.current_driver && (
              <p className="text-xs text-amber-300 mb-3 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                Currently assigned to <span className="font-semibold">{assignDriverTarget.current_driver}</span>
              </p>
            )}
            <label className="text-xs text-wt-text-muted">Select driver</label>
            <select
              value={selectedDriverId}
              onChange={(e) => setSelectedDriverId(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm"
              data-testid="mp-admin-driver-select"
            >
              <option value="">— Choose a driver —</option>
              {driverList.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.full_name || d.name || d.email}
                </option>
              ))}
            </select>
            {driverList.length === 0 && (
              <p className="text-xs text-amber-400 mt-2">
                No drivers found. Add a user with the &quot;driver&quot; role first.
              </p>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setAssignDriverTarget(null)}
                className="px-3 py-1.5 rounded text-xs text-wt-text-muted hover:text-wt-text-primary"
              >
                Cancel
              </button>
              <button
                onClick={submitAssignDriver}
                disabled={assigningDriver || !selectedDriverId}
                className="px-3 py-1.5 rounded bg-wt-gold text-black font-semibold text-xs disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="mp-admin-assign-driver-confirm"
              >
                <UserCog className="w-3 h-3" />
                {assigningDriver ? 'Assigning…' : (assignDriverTarget.current_driver ? 'Reassign' : 'Assign')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payout detail modal (Phase 4) */}
      {payoutDetail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setPayoutDetail(null)}>
          <div
            className="bg-wt-paper border border-wt-border rounded-2xl max-w-3xl w-full p-6 my-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="mp-admin-payout-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-wt-gold" /> Payout request
                </h2>
                <p className="text-xs text-wt-text-muted mt-0.5">
                  {payoutDetail.vendor_name} · {payoutDetail.po_ids?.length} POs
                  <span className={`ml-2 ${payoutPill(payoutDetail.status)}`} data-testid="mp-admin-payout-detail-status">{payoutDetail.status}</span>
                </p>
              </div>
              <button onClick={() => setPayoutDetail(null)} className="text-wt-text-muted hover:text-wt-text-primary">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <DetailRow label="Subtotal" value={`${payoutDetail.subtotal_amount} ${payoutDetail.currency}`} />
              <DetailRow label="Penalty" value={payoutDetail.penalty_amount ? `-${payoutDetail.penalty_amount}` : '—'} />
              <DetailRow label="Net amount" value={<b className="text-amber-400">{payoutDetail.net_amount} {payoutDetail.currency}</b>} />
              <DetailRow label="Invoice" value={
                payoutDetail.invoice_base64 ? (
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        const [meta, b64] = payoutDetail.invoice_base64.split(',');
                        const mime = (meta.match(/data:([^;]+)/) || ['', 'application/pdf'])[1];
                        const bin = atob(b64);
                        const buf = new Uint8Array(bin.length);
                        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
                        const url = URL.createObjectURL(new Blob([buf], { type: mime }));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = payoutDetail.invoice_filename || 'payout-invoice.pdf';
                        document.body.appendChild(a); a.click(); a.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                      } catch (_) { /* ignore */ }
                    }}
                    className="text-wt-gold hover:underline cursor-pointer"
                    data-testid="mp-admin-payout-invoice-link"
                  >
                    {payoutDetail.invoice_filename} ↓
                  </button>
                ) : '—'
              } />
              <DetailRow label="PO numbers" value={(payoutDetail.po_numbers || []).join(', ')} />
              {payoutDetail.zoho_bill_id && <DetailRow label="Zoho Bill" value={payoutDetail.zoho_bill_id} />}
              {payoutDetail.zoho_sync_error && <DetailRow label="Zoho error" value={<span className="text-rose-400">{payoutDetail.zoho_sync_error}</span>} />}
              {payoutDetail.bank_transfer_ref && <DetailRow label="Bank ref" value={`${payoutDetail.bank_transfer_ref} (${payoutDetail.bank_transfer_date})`} />}
              {payoutDetail.rejected_reason && <DetailRow label="Rejection reason" value={<span className="text-rose-400">{payoutDetail.rejected_reason}</span>} />}
            </div>

            {/* Adjustments — editable only when status=submitted */}
            <div className="border border-wt-border rounded-lg p-4 mb-4">
              <h3 className="text-sm font-semibold mb-2">Adjustments</h3>
              {(payoutDetail.adjustments || []).length === 0 ? (
                <div className="text-xs text-wt-text-muted mb-2">No adjustments applied.</div>
              ) : (
                <table className="w-full text-sm mb-2" data-testid="mp-admin-payout-adjustments">
                  <tbody>
                    {payoutDetail.adjustments.map((a) => (
                      <tr key={a.id} className="border-b border-wt-border/40">
                        <td className="py-1.5">{a.label}</td>
                        <td className="py-1.5 text-right text-amber-400">{a.amount > 0 ? '+' : ''}{a.amount}</td>
                        <td className="py-1.5 pl-3 w-8 text-right">
                          {payoutDetail.status === 'submitted' && (
                            <button
                              onClick={() => removeAdjustment(a.id)}
                              disabled={payoutAction}
                              className="text-rose-400 hover:text-rose-300"
                              data-testid={`mp-admin-payout-adj-remove-${a.id}`}
                            >×</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {payoutDetail.status === 'submitted' && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <input
                    placeholder="Label (e.g. On-time bonus / Damage offset)"
                    value={adjustmentDraft.label}
                    onChange={(e) => setAdjustmentDraft({ ...adjustmentDraft, label: e.target.value })}
                    className="flex-1 min-w-[200px] px-3 py-1.5 rounded bg-wt-paper border border-wt-border text-sm"
                    data-testid="mp-admin-payout-adj-label"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Amount (- for deduct)"
                    value={adjustmentDraft.amount}
                    onChange={(e) => setAdjustmentDraft({ ...adjustmentDraft, amount: e.target.value })}
                    className="w-32 px-3 py-1.5 rounded bg-wt-paper border border-wt-border text-sm"
                    data-testid="mp-admin-payout-adj-amount"
                  />
                  <button
                    onClick={addAdjustment}
                    disabled={payoutAction}
                    className="px-3 py-1.5 rounded bg-wt-gold text-wt-paper text-xs font-semibold"
                    data-testid="mp-admin-payout-adj-add"
                  >Add</button>
                </div>
              )}
            </div>

            {/* Actions per status */}
            <div className="flex flex-wrap items-center justify-end gap-2">
              {payoutDetail.status === 'submitted' && (
                <>
                  <button
                    onClick={() => {
                      const reason = window.prompt('Reason for rejection (required):', '');
                      if (reason && reason.trim()) payoutTransition('reject', { reason: reason.trim() });
                    }}
                    disabled={payoutAction}
                    className="px-3 py-2 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 text-xs"
                    data-testid="mp-admin-payout-reject"
                  >Reject</button>
                  <button
                    onClick={() => payoutTransition('approve')}
                    disabled={payoutAction}
                    className="px-4 py-2 rounded bg-blue-500 text-slate-950 text-sm font-semibold inline-flex items-center gap-1.5"
                    data-testid="mp-admin-payout-approve"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Approve
                  </button>
                </>
              )}
              {payoutDetail.status === 'approved' && (
                <div className="flex flex-wrap items-end gap-2 w-full">
                  <label className="text-xs text-wt-text-muted">
                    Bank transfer ref
                    <input
                      value={markPaidDraft.bank_transfer_ref}
                      onChange={(e) => setMarkPaidDraft({ ...markPaidDraft, bank_transfer_ref: e.target.value })}
                      className="mt-1 block w-48 px-3 py-1.5 rounded bg-wt-paper border border-wt-border text-sm"
                      data-testid="mp-admin-payout-bank-ref"
                    />
                  </label>
                  <label className="text-xs text-wt-text-muted">
                    Date
                    <DateInput
                      value={markPaidDraft.bank_transfer_date}
                      onChange={(e) => setMarkPaidDraft({ ...markPaidDraft, bank_transfer_date: e.target.value })}
                      className="mt-1 block px-3 py-1.5 rounded bg-wt-paper border border-wt-border text-sm"
                      data-testid="mp-admin-payout-bank-date"
                    />
                  </label>
                  <button
                    onClick={() => payoutTransition('mark-paid', markPaidDraft)}
                    disabled={payoutAction || !markPaidDraft.bank_transfer_ref.trim()}
                    className="ml-auto px-4 py-2 rounded bg-emerald-500 text-slate-950 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                    data-testid="mp-admin-payout-mark-paid"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Mark as paid
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminTab = ({ id, tab, setTab, icon, children }) => (
  <button
    onClick={() => setTab(id)}
    className={`px-4 py-2 -mb-px border-b-2 text-sm inline-flex items-center gap-1.5 transition-colors ${
      tab === id ? 'border-wt-gold text-wt-gold' : 'border-transparent text-wt-text-muted hover:text-wt-text-primary'
    }`}
    data-testid={`mp-admin-toptab-${id}`}
  >
    {icon} {children}
  </button>
);

const PO_STATUSES = [
  'pending_acceptance', 'accepted', 'ready_for_pickup',
  'picked_up', 'delivered', 'rejected', 'cancelled',
];

const AdminPOsSection = ({ adminPOs, poVendorFilter, poStatusFilter, setPOVendorFilter, setPOStatusFilter, loadPOs, onPenalty, onBookCourier, onAssignDriver, onOpenLinkedTickets }) => {
  useEffect(() => { loadPOs(); }, [poVendorFilter, poStatusFilter]); // eslint-disable-line
  return (
    <div className="bg-wt-surface border border-wt-border rounded-xl" data-testid="mp-admin-pos-section">
      <div className="border-b border-wt-border px-5 py-3 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-wt-text-primary">Vendor POs</h3>
        <select
          value={poStatusFilter}
          onChange={(e) => setPOStatusFilter(e.target.value)}
          className="ml-2 px-2 py-1 rounded bg-wt-paper border border-wt-border text-xs text-wt-text-primary"
          data-testid="mp-admin-po-status-filter"
        >
          <option value="">Any status</option>
          {PO_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <input
          value={poVendorFilter}
          onChange={(e) => setPOVendorFilter(e.target.value)}
          placeholder="Vendor id"
          className="px-2 py-1 rounded bg-wt-paper border border-wt-border text-xs text-wt-text-primary w-40"
          data-testid="mp-admin-po-vendor-filter"
        />
        <span className="text-[10px] text-wt-text-muted ml-auto">{adminPOs.length} POs</span>
      </div>
      {adminPOs.length === 0 ? (
        <div className="p-8 text-center text-sm text-wt-text-muted">No POs match the current filters.</div>
      ) : (
        <table className="w-full text-sm" data-testid="mp-admin-po-table">
          <thead className="text-xs text-wt-text-muted uppercase tracking-wide">
            <tr className="border-b border-wt-border">
              <th className="text-left px-4 py-2">Order</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Cost</th>
              <th className="text-right px-3 py-2">Commission</th>
              <th className="text-right px-3 py-2">TAT due</th>
              <th className="text-right px-3 py-2">Courier / AWB</th>
              <th className="text-right px-3 py-2">Penalty</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {adminPOs.map((p) => {
              const penaltyTotal = (p.penalties || []).reduce((s, x) => s + (x.amount || 0), 0);
              return (
                <tr key={p.id} className="border-b border-wt-border/50">
                  <td className="px-4 py-2 font-mono text-xs">
                    {p.order_number}
                    {p.ticket_count > 0 && (
                      <button
                        type="button"
                        onClick={() => onOpenLinkedTickets && onOpenLinkedTickets(p)}
                        className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 text-[10px] hover:bg-rose-500/25"
                        data-testid={`mp-admin-po-tickets-${p.id}`}
                        title={`${p.open_ticket_count || 0} open / ${p.ticket_count} total — click to view`}
                      >
                        <MessageSquare className="w-3 h-3" />
                        {p.open_ticket_count > 0 ? `${p.open_ticket_count} open` : p.ticket_count}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">{p.vendor_name}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded bg-wt-paper text-[10px] uppercase">{p.status.replace(/_/g, ' ')}</span>
                    {p.tat_breached && <span className="ml-2 text-[10px] text-rose-400">BREACHED</span>}
                  </td>
                  <td className="px-3 py-2 text-right">{p.subtotal_cost}</td>
                  <td className="px-3 py-2 text-right text-emerald-500">{p.commission_revenue}</td>
                  <td className="px-3 py-2 text-right text-xs text-wt-text-muted">{p.tat_due_at ? new Date(p.tat_due_at).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    {p.awb_number ? (
                      <span className="text-emerald-500" data-testid={`mp-admin-po-awb-${p.id}`}>
                        ✓ {p.recommended_courier || ''} · {p.awb_number}
                      </span>
                    ) : p.auto_book_status === 'needs_admin_review' || p.auto_book_status === 'no_rule_matched' || p.auto_book_status === 'missing_pickup_address' ? (
                      <span className="text-amber-500" data-testid={`mp-admin-po-bookstatus-${p.id}`}>
                        {p.auto_book_status?.replace(/_/g, ' ') || '—'}
                      </span>
                    ) : p.auto_book_status === 'skipped_company_pickup' ? (
                      <span className="text-slate-500">Pickup by us</span>
                    ) : (
                      <span className="text-wt-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-rose-400">{penaltyTotal || '—'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {p.shipping_mode === 'drop_ship' && !p.awb_number && (
                      <button
                        onClick={() => onBookCourier(p)}
                        className="mr-1 px-2 py-1 rounded bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 text-xs"
                        data-testid={`mp-admin-po-book-${p.id}`}
                      >
                        Book courier
                      </button>
                    )}
                    {/* Assign-driver appears only for company-pickup POs once the vendor
                        has marked them ready. Same UX as regular orders' assign flow. */}
                    {p.shipping_mode === 'pickup_from_vendor' &&
                      ['ready_for_pickup', 'picked_up', 'in_transit'].includes(p.status) && (
                      <button
                        onClick={() => onAssignDriver(p)}
                        className="mr-1 px-2 py-1 rounded bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 text-xs inline-flex items-center gap-1"
                        data-testid={`mp-admin-po-assign-driver-${p.id}`}
                        title={p.assigned_driver_name ? `Assigned to ${p.assigned_driver_name} · click to reassign` : 'Assign a driver for pickup'}
                      >
                        <Truck className="w-3 h-3" />
                        {p.assigned_driver_name ? `↻ ${p.assigned_driver_name.split(' ')[0]}` : 'Assign driver'}
                      </button>
                    )}
                    <button
                      onClick={() => onPenalty(p)}
                      className="px-2 py-1 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 text-xs"
                      data-testid={`mp-admin-po-penalty-${p.id}`}
                    >
                      + Penalty
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

const AdminPerformanceSection = ({ perfRows, reload }) => {
  useEffect(() => { reload(); }, []); // eslint-disable-line
  return (
    <div className="space-y-4" data-testid="mp-admin-performance-section">
      <div className="bg-wt-surface border border-wt-border rounded-xl">
        <div className="border-b border-wt-border px-5 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-wt-text-primary">Vendor performance roll-up</h3>
          <button onClick={reload} className="text-xs text-wt-text-muted hover:text-wt-text-primary inline-flex items-center gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        {perfRows.length === 0 ? (
          <div className="p-8 text-center text-sm text-wt-text-muted">No marketplace vendors yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="mp-admin-perf-table">
            <thead className="text-xs text-wt-text-muted uppercase tracking-wide">
              <tr className="border-b border-wt-border">
                <th className="text-left px-4 py-2">Vendor</th>
                <th className="text-right px-3 py-2">Total POs</th>
                <th className="text-right px-3 py-2">Acceptance</th>
                <th className="text-right px-3 py-2">Rejection</th>
                <th className="text-right px-3 py-2">Delay</th>
                <th className="text-right px-3 py-2">Defect</th>
                <th className="text-right px-3 py-2">Return</th>
                <th className="text-right px-3 py-2">Penalty (sum)</th>
                <th className="text-right px-3 py-2">Commission earned</th>
              </tr>
            </thead>
            <tbody>
              {perfRows.map((r) => (
                <tr key={r.vendor_id} className="border-b border-wt-border/50" data-testid={`mp-admin-perf-row-${r.vendor_id}`}>
                  <td className="px-4 py-2">{r.vendor_name}</td>
                  <td className="px-3 py-2 text-right">{r.total_pos}</td>
                  <td className="px-3 py-2 text-right text-emerald-500">{r.rates?.acceptance_rate_pct ?? 0}%</td>
                  <td className="px-3 py-2 text-right text-amber-500">{r.rates?.rejection_rate_pct ?? 0}%</td>
                  <td className="px-3 py-2 text-right text-orange-500">{r.rates?.delay_rate_pct ?? 0}%</td>
                  <td className="px-3 py-2 text-right text-rose-500">{r.rates?.defect_rate_pct ?? 0}%</td>
                  <td className="px-3 py-2 text-right text-pink-500">{r.rates?.return_rate_pct ?? 0}%</td>
                  <td className="px-3 py-2 text-right text-rose-500">{r.totals?.penalty_amount ?? 0}</td>
                  <td className="px-3 py-2 text-right text-emerald-500">{r.totals?.commission_revenue ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const SettingsField = ({ label, value, onChange, onBlur }) => (
  <label className="text-xs text-wt-text-muted">
    {label}
    <input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className="mt-1 w-full px-3 py-2 rounded bg-wt-paper border border-wt-border text-wt-text-primary text-sm"
    />
  </label>
);

const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-wt-border/40 text-sm">
    <span className="text-wt-text-muted">{label}</span>
    <span className="col-span-2 text-wt-text-primary break-words">{value || '—'}</span>
  </div>
);

const DocPreview = ({ label, base64, filename }) => {
  if (!base64) return (
    <div className="border border-dashed border-wt-border rounded p-4 text-center text-xs text-wt-text-muted">
      No {label} uploaded
    </div>
  );
  const isPdf = base64.startsWith('data:application/pdf');

  // Chrome blocks `data:` URLs in new tabs for security. Convert the
  // base64 into a Blob and trigger a real download so the user gets
  // the file every time, regardless of size.
  const downloadDataUrl = () => {
    try {
      const [meta, b64] = base64.split(',');
      const mime = (meta.match(/data:([^;]+)/) || ['', 'application/octet-stream'])[1];
      const bin = atob(b64);
      const buf = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
      const blob = new Blob([buf], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename
        || `${label.toLowerCase().replace(/\s+/g, '-')}.${mime.includes('pdf') ? 'pdf' : 'bin'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      // If decode fails (corrupt upload), fall back to opening the data URL.
      window.open(base64, '_blank');
    }
  };

  return (
    <div className="border border-wt-border rounded overflow-hidden">
      <div className="text-[10px] uppercase tracking-wide bg-wt-paper/40 px-2 py-1 text-wt-text-muted flex items-center gap-1">
        <FileText className="w-3 h-3" /> {label}
      </div>
      {isPdf ? (
        <button
          onClick={downloadDataUrl}
          className="w-full block p-4 text-center text-xs text-wt-gold hover:underline cursor-pointer"
          data-testid="doc-preview-download"
        >
          Download PDF
        </button>
      ) : (
        <img src={base64} alt={label} className="max-h-40 w-full object-contain bg-slate-950" />
      )}
    </div>
  );
};

const PAYOUT_STATUSES = ['draft', 'submitted', 'approved', 'paid', 'rejected'];

const payoutPill = (s) => {
  const map = {
    draft: 'bg-slate-500/15 text-slate-300',
    submitted: 'bg-amber-500/15 text-amber-300',
    approved: 'bg-blue-500/15 text-blue-300',
    paid: 'bg-emerald-500/15 text-emerald-300',
    rejected: 'bg-rose-500/15 text-rose-300',
  };
  return `px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${map[s] || 'bg-slate-500/15'}`;
};

const AdminTicketsSection = ({ rows, statusFilter, setStatusFilter, reload, onOpen }) => {
  useEffect(() => { reload(); }, [statusFilter]); // eslint-disable-line

  const CATS = {
    payment: 'Payment',
    po_issue: 'PO Issue',
    inventory: 'Inventory',
    onboarding: 'Onboarding',
    other: 'Other',
  };

  const pill = (s) => {
    const map = {
      open: 'bg-amber-500/15 text-amber-300',
      in_progress: 'bg-blue-500/15 text-blue-300',
      resolved: 'bg-emerald-500/15 text-emerald-300',
      closed: 'bg-slate-500/15 text-slate-400',
    };
    return `inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${map[s] || 'bg-slate-500/15 text-slate-300'}`;
  };

  return (
    <div className="bg-wt-surface border border-wt-border rounded-xl" data-testid="mp-admin-tickets-section">
      <div className="border-b border-wt-border px-5 py-3 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-wt-text-primary">Vendor support tickets</h3>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="ml-auto px-2 py-1 rounded bg-wt-paper border border-wt-border text-xs text-wt-text-primary"
          data-testid="mp-admin-ticket-status-filter"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <button onClick={reload} className="text-xs text-wt-text-muted hover:text-wt-text-primary inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-wt-text-muted">
          No support tickets {statusFilter ? `with status: ${statusFilter.replace('_', ' ')}` : 'yet'}.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="mp-admin-tickets-table">
            <thead>
              <tr className="border-b border-wt-border text-wt-text-muted">
                <th className="text-left px-3 py-2 font-medium">Subject</th>
                <th className="text-left px-3 py-2 font-medium">Vendor</th>
                <th className="text-left px-3 py-2 font-medium">Category</th>
                <th className="text-left px-3 py-2 font-medium">PO</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Last activity</th>
                <th className="text-right px-3 py-2 font-medium"> </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-b border-wt-border/40 hover:bg-wt-paper/40" data-testid={`mp-admin-ticket-row-${t.id}`}>
                  <td className="px-3 py-2 text-wt-text-primary font-medium max-w-xs truncate">{t.subject}</td>
                  <td className="px-3 py-2 text-wt-text-muted">{t.vendor_name}</td>
                  <td className="px-3 py-2 text-wt-text-muted">{CATS[t.category] || t.category}</td>
                  <td className="px-3 py-2 text-wt-gold font-mono">{t.related_po_number || '—'}</td>
                  <td className="px-3 py-2"><span className={pill(t.status)}>{(t.status || '').replace('_', ' ')}</span></td>
                  <td className="px-3 py-2 text-wt-text-muted">{(t.last_activity_at || '').replace('T', ' ').slice(0, 16)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => onOpen(t.id)}
                      className="px-2.5 py-1 rounded bg-wt-gold/15 text-wt-gold hover:bg-wt-gold/25 text-xs font-semibold inline-flex items-center gap-1"
                      data-testid={`mp-admin-ticket-open-${t.id}`}
                    >
                      <Eye className="w-3 h-3" /> Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AdminPayoutsSection = ({ rows, statusFilter, setStatusFilter, reload, onOpen }) => {
  useEffect(() => { reload(); }, [statusFilter]); // eslint-disable-line
  return (
    <div className="bg-wt-surface border border-wt-border rounded-xl" data-testid="mp-admin-payouts-section">
      <div className="border-b border-wt-border px-5 py-3 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-wt-text-primary">Vendor payout requests</h3>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="ml-2 px-2 py-1 rounded bg-wt-paper border border-wt-border text-xs text-wt-text-primary"
          data-testid="mp-admin-payout-status-filter"
        >
          <option value="">Any status</option>
          {PAYOUT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-[10px] text-wt-text-muted ml-auto">{rows.length} total</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-wt-text-muted">No payout requests yet.</div>
      ) : (
        <table className="w-full text-sm" data-testid="mp-admin-payout-table">
          <thead className="text-xs text-wt-text-muted uppercase tracking-wide">
            <tr className="border-b border-wt-border">
              <th className="text-left px-4 py-2">Submitted</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">POs</th>
              <th className="text-right px-3 py-2">Net</th>
              <th className="text-left px-3 py-2">Zoho bill</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-wt-border/50">
                <td className="px-4 py-2 text-xs">{new Date(p.submitted_at || p.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2">{p.vendor_name}</td>
                <td className="px-3 py-2"><span className={payoutPill(p.status)} data-testid={`mp-admin-payout-status-${p.id}`}>{p.status}</span></td>
                <td className="px-3 py-2 text-right">{p.po_ids?.length}</td>
                <td className="px-3 py-2 text-right text-amber-300">{p.net_amount} {p.currency}</td>
                <td className="px-3 py-2 text-xs">
                  {p.zoho_bill_id ? <span className="text-emerald-500">✓ {p.zoho_bill_id}</span>
                    : p.zoho_sync_error ? <span className="text-rose-400 cursor-help underline decoration-dotted" title={p.zoho_sync_error}>sync failed</span>
                    : <span className="text-wt-text-muted">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => onOpen(p.id)}
                    className="px-2 py-1 rounded bg-wt-paper text-xs hover:bg-wt-paper/70"
                    data-testid={`mp-admin-payout-open-${p.id}`}
                  >
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MarketplaceAdminPage;

// ──────── Visibility toggle card (Feb 2026) ────────
// Global default for: "Show commission % and selling price to vendors
// in their portal". Default OFF. Per-vendor override lives on
// MarketplaceVendorDetailPage.

const VisibilitySettingsCard = ({ authHeader }) => {
  const [show, setShow] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const me = JSON.parse(localStorage.getItem('user') || '{}');
    const cid = me.company_id || '';
    setCompanyId(cid);
    const qs = cid ? `?company_id=${cid}` : '';
    axios.get(`${API}/marketplace/admin/visibility-settings${qs}`, { headers: authHeader() })
      .then((r) => {
        if (!cancelled) {
          setShow(!!r.data?.show_commission_to_vendor);
          setLoaded(true);
        }
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [authHeader]);

  const onToggle = async (next) => {
    setShow(next);
    try {
      const qs = companyId ? `?company_id=${companyId}` : '';
      await axios.put(
        `${API}/marketplace/admin/visibility-settings${qs}`,
        { show_commission_to_vendor: next },
        { headers: authHeader() },
      );
      toast.success(next ? 'Vendors will now see commission & selling price' : 'Commission & selling price hidden from vendors');
    } catch (e) {
      setShow(!next);
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  if (!loaded) return null;
  return (
    <div className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid="mp-admin-visibility-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-wt-text-primary mb-0.5">Margin visibility</h3>
          <p className="text-xs text-wt-text-muted">
            By default vendors only see their <b>cost</b> — not your commission % or customer-facing selling price.
            Toggle below to enable visibility for ALL vendors, or override per-vendor on the vendor detail page.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={show}
            onChange={(e) => onToggle(e.target.checked)}
            className="accent-wt-gold"
            data-testid="mp-admin-visibility-toggle"
          />
          <span>{show ? 'Visible to vendors' : 'Hidden from vendors'}</span>
        </label>
      </div>
    </div>
  );
};


// ──────── Default Shopify warehouse for marketplace inventory pushes ────────
// Feb 2026 pass 5: explicit company-wide default. Vendor-level
// override on the detail page wins. Without one of these set,
// `push_inventory_quantity_to_shopify` is a no-op (we don't want
// to write into a random Shopify location by accident).

const DefaultWarehouseCard = ({ authHeader }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [whId, setWhId] = useState('');
  const [whName, setWhName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const me = JSON.parse(localStorage.getItem('user') || '{}');
    const cid = me.company_id || '';
    setCompanyId(cid);
    const qs = cid ? `?company_id=${cid}` : '';
    Promise.all([
      axios.get(`${API}/marketplace/admin/default-warehouse${qs}`, { headers: authHeader() }),
      axios.get(`${API}/locations`, { headers: authHeader() }),
    ])
      .then(([s, l]) => {
        if (cancelled) return;
        setWhId(s.data?.default_warehouse_id || '');
        setWhName(s.data?.default_warehouse_name || '');
        setWarehouses(l.data?.locations || []);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [authHeader]);

  const onSave = async (next) => {
    setSaving(true);
    try {
      const qs = companyId ? `?company_id=${companyId}` : '';
      await axios.put(
        `${API}/marketplace/admin/default-warehouse${qs}`,
        { default_warehouse_id: next || null },
        { headers: authHeader() },
      );
      setWhId(next || '');
      const w = warehouses.find((x) => x.id === next);
      setWhName(w?.name || '');
      toast.success(next ? `Default warehouse set to ${w?.name}` : 'Default warehouse cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;
  return (
    <div className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid="mp-admin-default-warehouse-card">
      <h3 className="text-sm font-semibold text-wt-text-primary mb-1">Default Shopify warehouse</h3>
      <p className="text-xs text-wt-text-muted mb-3">
        When a vendor updates inventory on their portal, Opswarex will mirror the new quantity into <b>this warehouse</b> on Shopify.
        You can override this per-vendor on the vendor detail page.
      </p>
      <select
        value={whId}
        disabled={saving}
        onChange={(e) => onSave(e.target.value)}
        className="w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm"
        data-testid="mp-admin-default-warehouse-select"
      >
        <option value="">— Not set (inventory pushes will be skipped) —</option>
        {warehouses.map((w) => (
          <option key={w.id} value={w.id} disabled={!w.shopify_location_id}>
            {w.name}{w.shopify_location_id ? '' : ' (no Shopify link)'}
          </option>
        ))}
      </select>
      {whName && (
        <p className="text-[10px] text-emerald-400 mt-2">
          Current default: <b>{whName}</b>
        </p>
      )}
      {!whId && (
        <p className="text-[10px] text-amber-400 mt-2">
          ⚠ Vendor inventory updates will <b>not</b> reach Shopify until you set a default (or set a per-vendor override).
        </p>
      )}
    </div>
  );
};



// ────────  Vendor → Shopify sync error banner (Feb 2026 pass 6)  ────────
// Surfaces any failed pushes from `push_inventory_quantity_to_shopify`
// so the admin knows when warehouse mapping is missing or Shopify
// creds are stale. Dismiss button clears all currently-listed errors.

const SyncErrorBanner = ({ authHeader }) => {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const load = () => {
    axios
      .get(`${API}/marketplace/admin/sync-errors`, { headers: authHeader() })
      .then((r) => {
        setRows(r.data?.rows || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissAll = async () => {
    if (!rows.length) return;
    if (!window.confirm(`Dismiss all ${rows.length} sync error(s)?`)) return;
    setDismissing(true);
    try {
      await axios.post(
        `${API}/marketplace/admin/sync-errors/dismiss`,
        { error_ids: null },
        { headers: authHeader() },
      );
      setRows([]);
      toast.success('Sync errors dismissed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setDismissing(false);
    }
  };

  const dismissOne = async (id) => {
    setDismissing(true);
    try {
      await axios.post(
        `${API}/marketplace/admin/sync-errors/dismiss`,
        { error_ids: [id] },
        { headers: authHeader() },
      );
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setDismissing(false);
    }
  };

  if (!loaded || rows.length === 0) return null;

  const labelFor = (t) => ({
    no_warehouse_mapped: 'No warehouse mapped',
    no_shopify_store: 'No Shopify store connected',
    no_shopify_variant: 'SKU has no Shopify variant',
    shopify_variant_not_found: 'Variant missing in Shopify',
    shopify_no_inventory_item: 'No inventory item on variant',
    shopify_api_error: 'Shopify API error',
  }[t] || t);

  return (
    <div
      className="border border-rose-500/40 bg-rose-500/10 rounded-xl p-4"
      data-testid="mp-admin-sync-error-banner"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-rose-300 font-semibold text-sm">
          <AlertTriangle className="w-4 h-4" />
          {rows.length} vendor inventory sync {rows.length === 1 ? 'failure' : 'failures'} — Shopify is out of sync
        </div>
        <button
          onClick={dismissAll}
          disabled={dismissing}
          className="text-xs text-rose-200 hover:text-white underline disabled:opacity-50"
          data-testid="mp-admin-sync-error-dismiss-all"
        >
          Dismiss all
        </button>
      </div>
      <ul className="text-xs text-wt-text-muted space-y-1 max-h-48 overflow-y-auto" data-testid="mp-admin-sync-error-list">
        {rows.slice(0, 25).map((e) => (
          <li key={e.id} className="flex items-start justify-between gap-3 py-1 border-b border-rose-500/10">
            <div className="min-w-0 flex-1">
              <span className="text-rose-300 font-medium">{labelFor(e.error_type)}</span>
              {' · '}
              <b className="text-wt-text-primary">{e.vendor_name || '—'}</b>
              {' · '}
              <span className="text-wt-text-primary">{e.sku}</span>
              {e.occurrence_count > 1 && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 text-[10px]">
                  ×{e.occurrence_count}
                </span>
              )}
              <div className="text-[11px] text-wt-text-muted mt-0.5 truncate">
                Attempted qty: {e.attempted_quantity} · {e.error_message}
              </div>
            </div>
            <button
              onClick={() => dismissOne(e.id)}
              disabled={dismissing}
              className="text-[11px] text-rose-200 hover:text-white shrink-0"
              data-testid={`mp-admin-sync-error-dismiss-${e.id}`}
            >
              Dismiss
            </button>
          </li>
        ))}
        {rows.length > 25 && (
          <li className="text-[11px] text-wt-text-muted pt-1">+{rows.length - 25} more…</li>
        )}
      </ul>
    </div>
  );
};
